import { useAuth } from "@/hooks/use-auth";
import { peekPitchReturn } from "@/lib/pitch-tour-partner";
import { Button } from "@/components/ui/button";
import { Eye, ArrowLeft } from "lucide-react";

// Single banner for impersonation flavours:
//   * Investor impersonation — `/api/auth/me` returns the impersonated
//     user wrapped with `isImpersonating: true` (role !== admin).
//   * Admin impersonation — same `isImpersonating` flag but the payload
//     is another admin (scoped teammate). Stay in /admin; stop returns
//     to Team.
//   * Partner (referral-agent) impersonation — `/api/auth/me` returns
//     the admin's own identity tagged with `isImpersonatingAgent: true`
//     plus an `impersonatingAgent` summary.
// Mutual exclusion is enforced server-side, so we'll never show both.
export function ImpersonationBanner() {
  const {
    user,
    isImpersonating,
    stopImpersonation,
    stopAdminImpersonation,
    isImpersonatingAgent,
    impersonatingAgent,
    stopAgentImpersonation,
  } = useAuth();

  if (isImpersonating && user) {
    const viewingAsAdmin = user.role === "admin";
    const handleStop = async () => {
      if (viewingAsAdmin) await stopAdminImpersonation();
      else await stopImpersonation();
    };
    return (
      <BannerShell
        label={
          <>
            Viewing as {viewingAsAdmin ? "admin " : ""}
            <strong>{user.name}</strong> ({user.email})
          </>
        }
        onStop={handleStop}
      />
    );
  }

  if (isImpersonatingAgent && impersonatingAgent) {
    const handleStop = async () => {
      await stopAgentImpersonation();
    };
    const backToPitch = peekPitchReturn() !== null;
    return (
      <BannerShell
        label={
          <>
            Viewing as partner <strong>{impersonatingAgent.companyName}</strong>{" "}
            ({impersonatingAgent.contactEmail})
          </>
        }
        stopLabel={backToPitch ? "Back to pitch" : "Back to Admin"}
        onStop={handleStop}
      />
    );
  }

  return null;
}

function BannerShell({
  label,
  onStop,
  stopLabel = "Back to Admin",
}: {
  label: React.ReactNode;
  onStop: () => void | Promise<void>;
  stopLabel?: string;
}) {
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-primary via-primary to-primary/95 text-brand-dark-bg px-4 py-2.5 flex items-center justify-between gap-3 shadow-[0_4px_24px_-4px_rgb(0_0_0_/_0.45)] border-b border-primary/30"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2.5 text-sm font-medium">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-brand-dark-bg/15 ring-1 ring-brand-dark-bg/10">
          <Eye className="w-3.5 h-3.5" />
        </span>
        <span className="tracking-tight">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-3 text-brand-dark-bg hover:bg-brand-dark-bg/15 font-semibold gap-1.5 rounded-md"
          onClick={() => void onStop()}
          data-testid="button-stop-impersonation"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {stopLabel}
        </Button>
      </div>
    </div>
  );
}
